import { useEffect, useState } from 'react'
import { api, type Device } from '../api'
import { radius, theme } from '../theme'
import { Button, Card } from './ui'
import { dateLocale, fromServer, t, tn } from '../lib/i18n'

/**
 * Device codes: a child's tablet signed in without Google (22 Oct 2026).
 *
 * Sam did not want his Google account on the boys' iPads: Google's window
 * signs Safari in to Google itself, so Gmail and YouTube could open as him,
 * and the iPad may offer to save the password. So, the way a TV signs in to a
 * streaming service: the grown-up's own phone shows six numbers (Settings →
 * Your account → Add a device), and they are typed on the tablet. The tablet
 * is then a child's device, which can do every child thing and none of the
 * account's. See services/devices.py on the server.
 */

const inputStyle = {
  width: '100%', boxSizing: 'border-box' as const, fontFamily: 'inherit', fontSize: 17,
  color: theme.text, background: '#FFFFFF', border: `1.5px solid ${theme.border}`,
  borderRadius: radius.md, padding: '12px 14px', minHeight: 48,
}

/** On the tablet: type the six numbers. */
export function DeviceCodeEntry({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [code, setCode] = useState('')
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const digits = code.replace(/\D/g, '').slice(0, 6)

  const go = async () => {
    setBusy(true)
    setProblem(null)
    try {
      await api.claimDevice(digits, label)
      onDone()
    } catch (err) {
      setProblem(fromServer(err, 'That did not work. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card style={{ maxWidth: 420, textAlign: 'left', marginTop: 16 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: theme.text, marginBottom: 6 }}>
        {t("Set up a child's tablet")}
      </div>
      <p style={{ margin: '0 0 14px', color: theme.text2, fontSize: 15, lineHeight: 1.5 }}>
        {/* One sentence, so each language can order it (round 13, 28 Oct 2026). */}
        {tn('On {phone}, open Word Sprout, then Settings → Your account → {add}. Type the six numbers it shows here. No Google sign-in happens on this tablet.',
            { phone: <b>{t('your own phone')}</b>, add: <b>{t('Add a device')}</b> })}
      </p>
      <label style={{ display: 'block', fontSize: 14, fontWeight: 700, color: theme.text2, marginBottom: 6 }}>
        {t('Device code')}
      </label>
      <input value={digits} onChange={(e) => setCode(e.target.value)} inputMode="numeric"
        autoComplete="one-time-code" placeholder="123456" aria-label={t('Device code')}
        style={{ ...inputStyle, fontSize: 26, letterSpacing: 6, textAlign: 'center', fontWeight: 800 }} />
      <label style={{ display: 'block', fontSize: 14, fontWeight: 700, color: theme.text2, margin: '14px 0 6px' }}>
        {t('Name this tablet (so you can find it in Settings)')}
      </label>
      <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40}
        placeholder="Mark's iPad" aria-label={t('Tablet name')} style={inputStyle} />
      {problem ? (
        <p style={{ margin: '12px 0 0', color: theme.red, fontSize: 15, lineHeight: 1.5 }}>{problem}</p>
      ) : null}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <Button onClick={() => void go()} disabled={busy || digits.length !== 6}>
          {busy ? t('Checking…') : t('Sign in this tablet')}
        </Button>
        <Button tone="ghost" onClick={onCancel} disabled={busy}>{t('Cancel')}</Button>
      </div>
    </Card>
  )
}

function ago(when: string | null): string {
  if (!when) return t('not used yet')
  const days = Math.floor((Date.now() - new Date(when).getTime()) / 86_400_000)
  return days <= 0 ? t('used today') : days === 1 ? t('used yesterday') : t('used {n} days ago', { n: days })
}

/** In Settings, on a grown-up's own device: make a code, see and remove tablets. */
export function DevicesPanel() {
  const [code, setCode] = useState<{ code: string; until: number } | null>(null)
  const [now, setNow] = useState(Date.now())
  const [list, setList] = useState<Device[] | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const load = () => { api.devices().then((r) => setList(r.devices)).catch(() => setList([])) }
  useEffect(load, [])

  // The countdown, and a fresh list once the code has been used or run out.
  useEffect(() => {
    if (!code) return
    const timer = window.setInterval(() => {
      setNow(Date.now())
      load()
    }, 5000)
    return () => window.clearInterval(timer)
  }, [code])

  const make = async () => {
    setProblem(null)
    try {
      const r = await api.makeDeviceCode()
      setCode({ code: r.code, until: Date.now() + r.expires_in * 1000 })
      setNow(Date.now())
    } catch (err) {
      setProblem(fromServer(err, 'That did not work.'))
    }
  }

  const remove = async (d: Device) => {
    if (!window.confirm(t('Sign out "{label}"? It will need a new code to come back.', { label: d.label }))) return
    await api.removeDevice(d.device_id).catch(() => undefined)
    load()
  }

  const left = code ? Math.max(0, Math.round((code.until - now) / 1000)) : 0

  return (
    <Card>
      <div style={{ fontSize: 17, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
        {t("Children's tablets")}
      </div>
      <p style={{ margin: '0 0 14px', color: theme.muted, fontSize: 15, lineHeight: 1.5 }}>
        {tn("Sign a child's tablet in without Google. On the tablet, tap {button} on the Word Sprout front page, then type the code shown here. A tablet set up this way can practise, but cannot reach billing or delete the account.",
            { button: <b>{t('Use a device code')}</b> })}
      </p>
      {code && left > 0 ? (
        <div style={{
          background: theme.greenPale, borderRadius: radius.md, padding: '12px 14px',
          textAlign: 'center', marginBottom: 12,
        }}>
          <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: 6, color: theme.greenDarker }}>
            {code.code.slice(0, 3)} {code.code.slice(3)}
          </div>
          <div style={{ fontSize: 14, color: theme.text2 }}>
            {t('Works once, for {time} more', { time: `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}` })}
          </div>
        </div>
      ) : null}
      <Button onClick={() => void make()}>{code ? t('New code') : t('Add a device')}</Button>
      {problem ? <p style={{ margin: '10px 0 0', color: theme.red, fontSize: 15 }}>{problem}</p> : null}
      {list && list.length ? (
        <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((d) => (
            <li key={d.device_id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
              border: `1px solid ${theme.border}`, borderRadius: radius.md, padding: '8px 12px',
            }}>
              <span>
                <b style={{ color: theme.text }}>{d.label}</b>
                <span style={{ color: theme.faint, fontSize: 14 }}>
                  {' · '}{t('added {date}', { date: new Date(d.added_at).toLocaleDateString(dateLocale()) })} · {ago(d.last_used_at)}

                </span>
              </span>
              <Button tone="ghost" onClick={() => void remove(d)}>{t('Remove')}</Button>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  )
}
